import { DiffContent, Entity, TrackingBuilder } from "../base";
import { Context } from "../base";
import { assertType } from "../base/utils";
import { CachingRef, IdentityRef, Ref } from './Ref';

class BlobContent implements Entity<BlobContent> {
  static STORE = "BlobContent";
  private _uuid: string;
  private _hash: string;
  private _content: string;

  constructor(uuid: string, hash: string, content: string) {
    assertType(uuid, "uuid", String);
    assertType(hash, "hash", String);
    assertType(content, "content", String);
    this._uuid = uuid;
    this._hash = hash;
    this._content = content;
  }

  get uuid(): string { return this._uuid; }
  get hash(): string { return this._hash; }
  get content(): string { return this._content; }


  getRef() {
    return new Ref(BlobContent, this._uuid);
  }

  getCachingRef() {
    return CachingRef.of(BlobContent, this._uuid, this);
  }

  builder(): BlobContentBuilder {
    return new BlobContentBuilder(this);
  }

  toJson(): BlobContentJSON {
    return {
      uuid: this._hash,
      hash: this._hash,
      content: this._content
    };
  }

  static identityRef = new IdentityRef(BlobContent);
  static identityValue = new BlobContent(BlobContent.identityRef.uuid, "", "");
  static identity(): BlobContent {
    return BlobContent.identityValue;
  }

  static fromUuid(uuid: string): BlobContent | undefined {
    const finder = Context.getInstance().getFinder(BlobContent);
    return finder.findById(uuid);
  }

  static createRef(uuid: string): Ref<BlobContent> {
    return CachingRef.of(BlobContent, uuid);
  }

  static fromJson(currency: BlobContentJSON): BlobContent {
    return new BlobContent(currency.uuid, currency.hash, currency.content);
  }
}

class BlobContentBuilder extends TrackingBuilder<BlobContent> {
  private _uuid: string;
  private _hash: string;
  private _content: string;
  private _diff: BlobContentDiff = {};

  constructor(object: BlobContent | undefined) {
    super();
    const obj = object ? object : BlobContent.identity();
    this._uuid = obj.uuid;
    this._hash = obj.hash;
    this._content = obj.content;
  }

  uuid(uuid: string) {
    if (this.isTracking() && this._uuid !== uuid) {
      this._diff.uuid = uuid;
    }
    this._uuid = uuid;
    return this;
  }

  hash(hash: string) {
    if (this.isTracking() && this._hash !== hash) {
      this._diff.hash = hash;
    }
    this._hash = hash;
    return this;
  }

  content(content: string) {
    if (this.isTracking() && this._content !== content) {
      this._diff.content = content;
    }
    this._content = content;
    return this;
  }

  build(): BlobContent {
    return new BlobContent(this._uuid, this._hash, this._content);
  }

  applyDiff(diff: BlobContentDiff): BlobContentBuilder {
    if (diff.uuid) {
      this.uuid(diff.uuid);
    }
    if (diff.hash) {
      this.hash(diff.hash);
    }
    if (diff.content) {
      this.content(diff.content);
    }
    return this;
  }

  applyObj(object: BlobContent): BlobContentBuilder {
    this.hash(object.hash);
    this.content(object.content);
    this.uuid(object.uuid);
    return this;
  }

  getDiff(): BlobContentDiff {
    return this._diff;
  }
}

interface BlobContentJSON {
  uuid: string,
  hash: string,
  content: string
}

interface BlobContentDiff extends DiffContent<BlobContent> {
  uuid?: string,
  hash?: string,
  content?: string
}

export { BlobContent, BlobContentJSON, BlobContentDiff, BlobContentBuilder }